<script setup>
/* eslint-disable */
import { ref, computed, Teleport } from 'vue'
import { useGameStore } from '@/store/game'
import { GameActions, GameActionTypes } from '@/utils/gameActions'
import { GaiaCalendar } from '@/utils/gaiaCalendar'
import { getStatGrade, getStatGradeClass, GRADE_VALUES  } from '@/utils/chocoboStats'
import { getChocoboColorClass } from '@/utils/colorUtils'
import { applyThemeClasses } from '@/utils/themeColors'
import TransitOverlay from './TransitOverlay.vue'


const props = defineProps({
  chocobo: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close'])
const gameStore = useGameStore()
const showConfirmation = ref(false)
const selectedRoute = ref(null)
const showLoading = ref(false)

const TRANSIT_ROUTES = {
  LOCAL_DELIVERY: {
    id: 'local',
    name: 'Local Delivery Route',
    dailyEarnings: 500,
    description: 'Journey in the local region',
    requirements: {
      minSpeed: 40,
      minStamina: 30
    }
  },
  REGIONAL_TRANSPORT: {
    id: 'regional',
    name: 'Midgar to Junon',
    dailyEarnings: 750,
    description: 'Journey between major cities',
    requirements: {
      minSpeed: 70, 
      minStamina: 60 
    }
  },
  CROSS_CONTINENT: {
    id: 'continental',
    name: 'Condor to Corel',
    dailyEarnings: 1500,
    description: 'Travel across continents',
    requirements: {
      minSpeed: 90,
      minStamina: 80
    }
  }
}

const getRequiredGrade = (statValue) => {
  return getStatGrade(statValue)
}

const meetsRequirements = (route) => {
  const chocoboSpeedGrade = getStatGrade(props.chocobo.speed)
  const chocoboStaminaGrade = getStatGrade(props.chocobo.stamina)
  const requiredSpeedGrade = getRequiredGrade(route.requirements.minSpeed)
  const requiredStaminaGrade = getRequiredGrade(route.requirements.minStamina)
  
  return GRADE_VALUES[chocoboSpeedGrade] >= GRADE_VALUES[requiredSpeedGrade] &&
         GRADE_VALUES[chocoboStaminaGrade] >= GRADE_VALUES[requiredStaminaGrade]
}


const getWeeklyEarnings = (route) => {
  return route.dailyEarnings * 7
}


const getNextDate = computed(() => {
  return GaiaCalendar.formatGameDate(gameStore.currentDay + 7)
})

const confirmRoute = (route) => {
  selectedRoute.value = route
  showConfirmation.value = true
}

const assignToRoute = async () => {
  if (!selectedRoute.value) return
  showConfirmation.value = false
  showLoading.value = true
  
  try {
    // Add a delay before the action to make the loading state visible
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    await GameActions.performAction(GameActionTypes.TRANSIT, {
      chocobo: props.chocobo,
      route: selectedRoute.value
    })
    emit('close')
  } catch (error) {
    console.error('Transit assignment failed:', error)
  } finally {
    showLoading.value = false
  }
}

</script>

<template>
  <div :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`">
    <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-h-[90vh] overflow-y-auto p-6`">
      <div :class="`flex justify-between ${applyThemeClasses('components.modal.container')} items-center sticky top-0 p-4 border-b`">
        <h2 :class="applyThemeClasses('text.primary')">Assign to Transit Route</h2>
        <button 
          @click="$emit('close')"
          class="text-gray-500 hover:text-gray-700"
        >
          ✕
        </button>
      </div>

      <div class="p-4">
        <div :class="`rounded-lg shadow-lg ${applyThemeClasses('layout.card')} mb-6`">
            <div class="flex items-center gap-4">
            <div :class="[getChocoboColorClass(chocobo.color), 'w-16 h-16 rounded-lg']"></div>
            <div>
                <h3 :class="applyThemeClasses('text.primaryLow')">{{ chocobo.name }}</h3>
                <div class="flex gap-4 mt-2">
                <span 
                    :class="[
                    getStatGradeClass(getStatGrade(chocobo.speed)),
                    'px-2 py-1 rounded text-sm font-medium'
                    ]"
                >
                    Speed: {{ getStatGrade(chocobo.speed) }}
                </span>
                <span 
                    :class="[
                    getStatGradeClass(getStatGrade(chocobo.stamina)),
                    'px-2 py-1 rounded text-sm font-medium'
                    ]"
                >
                    Stamina: {{ getStatGrade(chocobo.stamina) }}
                </span>
                </div>
            </div>
            </div>
        </div>
        <div class="space-y-4">
          <div
            v-for="route in TRANSIT_ROUTES"
            :key="route.id"
            :class="[
              'border rounded-lg p-4 transition-colors',
              meetsRequirements(route)
                ? 'cursor-pointer hover:bg-blue-50'
                : 'opacity-50 cursor-not-allowed bg-gray-50'
            ]"
            @click="meetsRequirements(route) && confirmRoute(route)"
          >
            <div class="flex justify-between items-start">
              <div>
                <h4 :class="applyThemeClasses('text.secondaryBold')">{{ route.name }}</h4>
                <p :class="`${applyThemeClasses('text.secondary')} text-sm`">{{ route.description }}</p>
                <div class="mt-2 flex gap-4">
                    <span 
                    :class="[
                        getStatGradeClass(getRequiredGrade(route.requirements.minSpeed)),
                        'px-2 py-1 rounded text-sm'
                    ]"
                    >
                    Speed {{ getRequiredGrade(route.requirements.minSpeed) }} or higher
                    </span>
                    <span 
                    :class="[
                        getStatGradeClass(getRequiredGrade(route.requirements.minStamina)),
                        'px-2 py-1 rounded text-sm'
                    ]"
                    >
                    Stamina {{ getRequiredGrade(route.requirements.minStamina) }} or higher
                    </span>
                </div>
                </div>
                <div class="text-right">
                <div class="font-semibold text-green-600">
                    <span :class="applyThemeClasses('text.money')">{{ getWeeklyEarnings(route).toLocaleString() }} G/week</span>
                </div>
                <div class="text-sm text-gray-500">
                  <span :class="`${applyThemeClasses('text.secondary')} text-sm`">({{ route.dailyEarnings.toLocaleString() }} G/day)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div 
      v-if="showConfirmation"
      :class="`fixed inset-0 ${applyThemeClasses('components.modal.overlay')} flex items-center justify-center z-50`"
    >
      <div :class="`rounded-lg ${applyThemeClasses('components.modal.container')} max-w-4xl w-full m-4 p-2`">
        <h3 :class="`${applyThemeClasses('text.primary')}`">Confirm Transit Assignment</h3>
        <p :class="`${applyThemeClasses('text.secondary')}`">
          Take {{ chocobo.name }} with you from {{ selectedRoute.name }}?
        </p>
        <p :class="`${applyThemeClasses('text.secondary')} text-sm`">
          You and your chocobo will be unavailable until <span class="font-medium">{{ getNextDate }}</span>
        </p>
        <div class="mt-4 text-green-600 font-medium">
          Weekly Earnings: {{ getWeeklyEarnings(selectedRoute).toLocaleString() }}G
        </div>
        <div class="mt-6 flex justify-end space-x-4">
          <button 
            @click="showConfirmation = false"
            class="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            <span :class="`${applyThemeClasses('text.secondary')}`">Cancel</span>
          </button>
          <button 
            @click="assignToRoute"
            class="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Confirm Assignment
          </button>
        </div>
      </div>
    </div>
  </div>
  <Teleport to="body">
    <TransitOverlay v-if="showLoading" />
  </Teleport>
</template>